import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "./utils";
import type {
  LibraryGroupsGetResponse,
  LibraryGroupGetResponse,
  LibraryGroupPatchRequest,
  LibraryGroupPatchResponse,
  LibraryGroupPostRequest,
  LibraryGroupPostResponse,
  PersonalLibraryCreateResponse,
  PersonalLibraryDeleteResponse,
} from "../../../api/src/library-groups/route";
import type {
  LibraryGroupRenderAllResponse,
  LibraryGroupRenderResponse,
} from "../../../api/src/library-groups/render";
import type { PersonalLibraryCreateRequest } from "../../../api/src/library-groups/personal-helpers";

export const libraryGroupsApi = createApi({
  reducerPath: "libraryGroupsApi",
  baseQuery,
  tagTypes: ["libraryGroups", "libraryGroupRenders"],
  endpoints: (builder) => ({
    getLibraryGroups: builder.query<
      LibraryGroupsGetResponse,
      { stateId: string | undefined; documentId: string | undefined }
    >({
      query: ({ stateId, documentId }) => ({
        url: `library-groups`,
        params: {
          ...(stateId && { stateId }),
          ...(documentId && { documentId }),
        },
        method: "GET",
      }),
      providesTags: ["libraryGroups"],
    }),
    getLibraryGroupById: builder.query<LibraryGroupGetResponse, string>({
      query: (id) => ({ url: `library-groups/${id}`, method: "GET" }),
      providesTags: (_r, _e, id) => [{ type: "libraryGroups", id }],
    }),
    renderAllLibraryGroups: builder.query<
      LibraryGroupRenderAllResponse,
      { documentId: string; skipUserData?: boolean }
    >({
      query: ({ documentId, skipUserData }) => ({
        url: `documents/${documentId}/library-groups/render-all`,
        params: { ...(skipUserData && { skipUserData: "true" }) },
        method: "GET",
      }),
      providesTags: (result) =>
        result?.groups.map((g) => ({
          type: "libraryGroupRenders" as const,
          id: g.groupId,
        })) ?? [],
    }),
    renderLibraryGroup: builder.query<
      LibraryGroupRenderResponse,
      { documentId: string; groupId: string }
    >({
      query: ({ documentId, groupId }) => ({
        url: `documents/${documentId}/library-groups/${groupId}/render`,
        method: "GET",
      }),
      providesTags: (_result, _error, { groupId }) => [
        { type: "libraryGroupRenders", id: groupId },
      ],
    }),
    createLibraryGroup: builder.mutation<
      LibraryGroupPostResponse,
      LibraryGroupPostRequest
    >({
      query: (body) => ({ url: `library-groups`, method: "POST", body }),
      invalidatesTags: ["libraryGroups"],
    }),
    updateLibraryGroup: builder.mutation<
      LibraryGroupPatchResponse,
      { id: string } & LibraryGroupPatchRequest
    >({
      query: ({ id, ...patch }) => ({
        url: `library-groups/${id}`,
        method: "PATCH",
        body: patch,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        "libraryGroups",
        { type: "libraryGroups", id },
        { type: "libraryGroupRenders", id },
      ],
    }),
    createPersonalLibraryGroup: builder.mutation<
      PersonalLibraryCreateResponse,
      PersonalLibraryCreateRequest
    >({
      query: (body) => ({
        url: `library-groups/personal`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["libraryGroups"],
    }),
    updatePersonalLibraryGroup: builder.mutation<
      PersonalLibraryCreateResponse,
      { id: string } & PersonalLibraryCreateRequest
    >({
      query: ({ id, ...body }) => ({
        url: `library-groups/${id}/personal`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        "libraryGroups",
        { type: "libraryGroups", id },
        { type: "libraryGroupRenders", id },
      ],
    }),
    deletePersonalLibraryGroup: builder.mutation<
      PersonalLibraryDeleteResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `library-groups/${id}/personal`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { id }) => [
        "libraryGroups",
        { type: "libraryGroups", id },
        { type: "libraryGroupRenders", id },
      ],
    }),
  }),
});

export const {
  useGetLibraryGroupsQuery,
  useGetLibraryGroupByIdQuery,
  useCreateLibraryGroupMutation,
  useUpdateLibraryGroupMutation,
  useRenderAllLibraryGroupsQuery,
  useRenderLibraryGroupQuery,
  useCreatePersonalLibraryGroupMutation,
  useUpdatePersonalLibraryGroupMutation,
  useDeletePersonalLibraryGroupMutation,
} = libraryGroupsApi;
